import type {
  MouseEventHandler,
  PropsWithChildren,
  ReactNode,
  Ref,
} from 'react'
import React, { useRef } from 'react'
import { useSyncRefs } from '../hooks/useSyncRefs'
import Menu from '../Menu/Menu'
import { useSelect } from './Select'

/**
 * OptionProps does not include a wrapText prop directly.
 * Wrapping is controlled by the parent Select via context.
 */
export interface OptionProps {
  /**
   * If true, the option is disabled
   * @default false
   */
  disabled?: boolean
  /** Custom display value to show when this option is selected */
  displayValue?: string
  /**
   * The content to display for the option
   */
  children: ReactNode
  /**
   * Value associated with the option
   * @default ''
   */
  value?: string
}

function Option(
  {
    children,
    disabled = false,
    displayValue,
    value = '',
  }: PropsWithChildren<OptionProps>,
  ref: Ref<HTMLButtonElement>,
): JSX.Element {
  const [state] = useSelect()
  const selected = state.value === value
  const wrapText = state.wrapText

  const internalRef = useRef<HTMLButtonElement>(null)
  const optionRef = useSyncRefs(internalRef, ref, (element) => {
    if (selected && state.displayInputRef.current && element?.textContent) {
      if (state.inputRef.current) {
        state.inputRef.current.value = state.value || ''
      }
      state.displayInputRef.current.value = state.value
        ? displayValue || element.textContent
        : ''
    }
  })

  const handleClick: MouseEventHandler<HTMLButtonElement> = (event) => {
    if (!disabled) {
      const input = state.inputRef.current
      if (input) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(input, value)
        input.dispatchEvent(inputEvent)
      }

      const display = state.displayInputRef.current
      if (display) {
        const descriptor = Object.getOwnPropertyDescriptor(
          window.HTMLInputElement.prototype,
          'value',
        ) as PropertyDescriptor
        const inputEvent = new Event('input', { bubbles: true })

        descriptor.set?.call(
          display,
          value ? displayValue || event.currentTarget.textContent : '',
        )
        display.dispatchEvent(inputEvent)
      }
    }
  }

  return (
    <Menu.Item
      disabled={disabled}
      onClick={handleClick}
      ref={optionRef}
      selected={selected}
      wrapText={wrapText}
    >
      {children}
    </Menu.Item>
  )
}

export default Option
